<template>
  <div class="post-content max-w-none">
    <!-- Table of Contents (if enabled) -->
    <div v-if="showToc && tocItems.length" class="toc-container mb-8 lg:hidden">
      <div class="bg-white rounded-xl shadow-xl border border-white/20 p-6">
        <h3 class="text-lg font-bold text-gray-900 mb-4 tracking-tight">
          Índice de contenidos
        </h3>
        <nav class="space-y-2">
          <a
            v-for="item in tocItems"
            :key="item.id"
            :href="`#${item.id}`"
            @click="scrollToHeading($event, item.id)"
            :class="[
              'block text-sm hover:text-teal-600 transition-colors duration-200',
              getHeadingClass(item.level)
            ]"
          >
            {{ item.text }}
          </a>
        </nav>
      </div>
    </div>

    <!-- Main Content -->
    <div 
      ref="contentRef"
      class="prose prose-lg prose-gray max-w-none"
      :class="contentClasses"
      v-html="processedContent"
    />

    <!-- Legal Disclaimer (if legal content detected) -->
    <div v-if="hasLegalContent" class="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
      <div class="flex items-start space-x-3">
        <ExclamationTriangleIcon class="w-5 h-5 text-yellow-600 mt-1" />
        <div class="text-sm">
          <p class="font-semibold text-yellow-800 mb-1">Aviso Legal</p>
          <p class="text-yellow-700">
            Esta información tiene carácter orientativo y no constituye asesoramiento jurídico. 
            Para casos específicos, consulte con un profesional legal cualificado.
          </p>
        </div>
      </div>
    </div>

    <!-- Image Modal -->
    <div 
      v-if="showImageModal"
      class="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      @click="closeImageModal"
    >
      <div class="relative max-w-5xl max-h-full">
        <img
          :src="modalImage.src"
          :alt="modalImage.alt"
          class="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
        />
        <button
          @click="closeImageModal"
          class="absolute top-4 right-4 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-200"
        >
          <XMarkIcon class="w-6 h-6" />
        </button>
        <div v-if="modalImage.caption" class="absolute bottom-4 left-4 right-4">
          <div class="bg-black/70 text-white text-sm p-3 rounded-lg">
            {{ modalImage.caption }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUpdated, nextTick } from 'vue'
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import DOMPurify from 'dompurify'

// Props
const props = defineProps({
  content: {
    type: String,
    required: true
  },
  showToc: {
    type: Boolean,
    default: false
  },
  enableImageModal: {
    type: Boolean,
    default: true
  },
  enableCodeHighlight: {
    type: Boolean,
    default: true
  },
  sanitize: {
    type: Boolean,
    default: true
  }
})

// Refs
const contentRef = ref(null)
const showImageModal = ref(false)
const modalImage = ref({ src: '', alt: '', caption: '' })
const tocItems = ref([])

// Computed
const processedContent = computed(() => {
  if (!props.content) return ''
  
  let content = props.content
  
  // Process headings for TOC and add IDs
  content = processHeadings(content)
  
  // Process images for modal and lazy loading
  if (props.enableImageModal) {
    content = processImages(content)
  }
  
  // Process code blocks for highlighting
  if (props.enableCodeHighlight) {
    content = processCodeBlocks(content)
  }
  
  // Add legal styling to specific content
  content = processLegalContent(content)
  
  // Sanitize HTML if enabled
  if (props.sanitize) {
    content = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 'strike', 'a', 'ul', 'ol', 'li',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code',
        'img', 'figure', 'figcaption', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'div', 'span', 'mark', 'sup', 'sub'
      ],
      ALLOWED_ATTR: [
        'href', 'target', 'rel', 'src', 'alt', 'title', 'width', 'height',
        'class', 'id', 'loading'
      ]
    })
  }
  
  return content
})

const hasLegalContent = computed(() => {
  if (!props.content) return false
  
  const legalKeywords = [
    'GDPR', 'RGPD', 'derecho al olvido', 'protección de datos',
    'artículo 17', 'responsable del tratamiento', 'consentimiento',
    'autoridad de control', 'expediente sancionador'
  ]
  
  return legalKeywords.some(keyword => 
    props.content.toLowerCase().includes(keyword.toLowerCase())
  )
})

const contentClasses = computed(() => {
  return [
    // Tailwind Prose customizations
    'prose-headings:tracking-tight',
    'prose-headings:font-bold',
    'prose-h1:text-3xl prose-h1:text-gray-900',
    'prose-h2:text-2xl prose-h2:text-gray-800 prose-h2:border-b prose-h2:border-gray-200 prose-h2:pb-2',
    'prose-h3:text-xl prose-h3:text-gray-800',
    'prose-h4:text-lg prose-h4:text-gray-700',
    'prose-p:leading-relaxed prose-p:text-gray-700',
    'prose-a:text-teal-600 prose-a:no-underline hover:prose-a:text-teal-800 hover:prose-a:underline',
    'prose-strong:text-gray-900 prose-strong:font-semibold',
    'prose-ul:my-4 prose-ol:my-4',
    'prose-li:my-1',
    'prose-blockquote:border-l-4 prose-blockquote:border-teal-500 prose-blockquote:bg-teal-50 prose-blockquote:p-4 prose-blockquote:my-6',
    'prose-blockquote:text-teal-800 prose-blockquote:font-medium prose-blockquote:italic',
    'prose-pre:bg-gray-800 prose-pre:text-gray-100 prose-pre:rounded-lg prose-pre:p-4',
    'prose-code:bg-gray-100 prose-code:text-gray-800 prose-code:px-1 prose-code:py-0.5 prose-code:rounded',
    'prose-table:my-6 prose-table:w-full prose-table:border-collapse',
    'prose-th:bg-gray-50 prose-th:border prose-th:border-gray-300 prose-th:p-3 prose-th:text-left prose-th:font-semibold',
    'prose-td:border prose-td:border-gray-300 prose-td:p-3',
    'prose-img:rounded-lg prose-img:shadow-lg prose-img:my-6 prose-img:cursor-pointer',
    'prose-figure:my-6 prose-figcaption:text-center prose-figcaption:text-sm prose-figcaption:text-gray-600 prose-figcaption:mt-2'
  ]
})

// Methods
const processHeadings = (content) => {
  tocItems.value = []
  
  return content.replace(/<(h[1-6])([^>]*)>(.*?)<\/h[1-6]>/gi, (match, tag, attrs, text) => {
    const level = parseInt(tag.charAt(1))
    const id = generateHeadingId(text)
    
    // Add to TOC
    tocItems.value.push({
      id,
      text: text.replace(/<[^>]*>/g, ''),
      level
    })
    
    // Return heading with ID
    return `<${tag} id="${id}" ${attrs}>${text}</${tag}>`
  })
}

const processImages = (content) => {
  return content.replace(/<img([^>]+)>/gi, (match, attrs) => {
    const srcMatch = attrs.match(/src\s*=\s*"([^"]+)"/i)
    const altMatch = attrs.match(/alt\s*=\s*"([^"]+)"/i)
    const titleMatch = attrs.match(/title\s*=\s*"([^"]+)"/i)
    
    if (!srcMatch) return match
    
    const src = srcMatch[1]
    const alt = altMatch ? altMatch[1] : ''
    const title = titleMatch ? titleMatch[1] : ''
    
    // Add click handler class and loading attribute
    let processedAttrs = attrs
    if (!processedAttrs.includes('loading=')) {
      processedAttrs += ' loading="lazy"'
    }
    if (!processedAttrs.includes('class=')) {
      processedAttrs += ' class="clickable-image"'
    } else {
      processedAttrs = processedAttrs.replace(/class\s*=\s*"([^"]*)"/, 'class="$1 clickable-image"')
    }
    
    return `<img${processedAttrs} data-modal-src="${src}" data-modal-alt="${alt}" data-modal-caption="${title}">`
  })
}

const processCodeBlocks = (content) => {
  // This would integrate with a syntax highlighter like Prism.js or highlight.js
  // For now, we'll just ensure proper styling
  return content.replace(/<pre><code([^>]*)>([\s\S]*?)<\/code><\/pre>/gi, (match, attrs, code) => {
    const langMatch = attrs.match(/class\s*=\s*"(?:language-)?([^"]+)"/i)
    const lang = langMatch ? langMatch[1] : 'text'
    
    return `<pre class="language-${lang}"><code class="language-${lang}">${code}</code></pre>`
  })
}

const processLegalContent = (content) => {
  // Highlight legal terms
  const legalTerms = [
    { term: 'GDPR', replacement: '<mark class="bg-blue-100 text-blue-800 px-1 rounded">GDPR</mark>' },
    { term: 'RGPD', replacement: '<mark class="bg-blue-100 text-blue-800 px-1 rounded">RGPD</mark>' },
    { term: 'derecho al olvido', replacement: '<mark class="bg-teal-100 text-teal-800 px-1 rounded">derecho al olvido</mark>' },
    { term: 'Artículo 17', replacement: '<mark class="bg-yellow-100 text-yellow-800 px-1 rounded">Artículo 17</mark>' }
  ]
  
  let processedContent = content
  
  legalTerms.forEach(({ term, replacement }) => {
    const regex = new RegExp(`\\b${term}\\b`, 'gi')
    processedContent = processedContent.replace(regex, replacement)
  })
  
  return processedContent
}

const generateHeadingId = (text) => {
  return text
    .toLowerCase()
    .replace(/<[^>]*>/g, '')
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/--+/g, '-')
    .trim()
}

const getHeadingClass = (level) => {
  const classes = {
    1: 'font-bold text-gray-900',
    2: 'font-semibold text-gray-800 ml-2',
    3: 'font-medium text-gray-700 ml-4',
    4: 'font-medium text-gray-600 ml-6',
    5: 'text-gray-600 ml-8',
    6: 'text-gray-500 ml-10'
  }
  
  return classes[level] || classes[3]
}

const scrollToHeading = (event, id) => {
  event.preventDefault()
  const element = document.getElementById(id)
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
    
    // Update URL
    window.history.replaceState(null, null, `#${id}`)
  }
}

const openImageModal = (src, alt, caption) => {
  modalImage.value = { src, alt, caption }
  showImageModal.value = true
  document.body.style.overflow = 'hidden'
}

const closeImageModal = () => {
  showImageModal.value = false
  document.body.style.overflow = ''
}

const setupImageClickHandlers = () => {
  if (!contentRef.value) return
  
  const images = contentRef.value.querySelectorAll('img.clickable-image')
  
  images.forEach(img => {
    img.addEventListener('click', () => {
      const src = img.dataset.modalSrc || img.src
      const alt = img.dataset.modalAlt || img.alt || ''
      const caption = img.dataset.modalCaption || img.title || ''
      
      openImageModal(src, alt, caption)
    })
  })
}

// Lifecycle
onMounted(() => {
  nextTick(() => {
    setupImageClickHandlers()
  })
})

onUpdated(() => {
  nextTick(() => {
    setupImageClickHandlers()
  })
})
</script>

<style scoped>
@reference "../../assets/styles/main.css";

/* Post content styling */
.post-content {
  line-height: 1.8;
}

/* TOC styling */
.toc-container nav a {
  padding: 0.25rem 0;
  border-left: 2px solid transparent;
  padding-left: 0.5rem;
}

.toc-container nav a:hover {
  border-left-color: theme('colors.teal.500');
  background-color: theme('colors.teal.50');
}

/* Custom prose enhancements */
.prose :where(blockquote):not(:where([class~="not-prose"] *)) {
  position: relative;
}

.prose :where(blockquote):not(:where([class~="not-prose"] *))::before {
  content: '"';
  position: absolute;
  top: -0.5rem;
  left: 0.5rem;
  font-size: 3rem;
  color: theme('colors.teal.300');
  line-height: 1;
}

/* Code block enhancements */
.prose :where(pre):not(:where([class~="not-prose"] *)) {
  position: relative;
  overflow-x: auto;
}

.prose :where(pre code):not(:where([class~="not-prose"] *)) {
  background: transparent;
  padding: 0;
  font-size: 0.875rem;
  line-height: 1.5;
}

/* Table enhancements */
.prose :where(table):not(:where([class~="not-prose"] *)) {
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

/* Image enhancements */
.prose :where(img):not(:where([class~="not-prose"] *)) {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.prose :where(img.clickable-image):not(:where([class~="not-prose"] *)) {
  cursor: pointer;
}

.prose :where(img.clickable-image):not(:where([class~="not-prose"] *)):hover {
  transform: scale(1.02);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Legal content highlights */
:deep(mark) {
  border-radius: 0.25rem;
  padding: 0.125rem 0.25rem;
  font-weight: 500;
}

/* Focus states */
.toc-container nav a:focus {
  outline: 2px solid theme('colors.teal.500');
  outline-offset: 2px;
  border-radius: 0.25rem;
}

/* Modal styling */
.fixed.inset-0 {
  backdrop-filter: blur(4px);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .prose {
    font-size: 1rem;
  }
  
  .prose :where(h1):not(:where([class~="not-prose"] *)) {
    font-size: 1.875rem;
  }
  
  .prose :where(h2):not(:where([class~="not-prose"] *)) {
    font-size: 1.5rem;
  }
  
  .prose :where(h3):not(:where([class~="not-prose"] *)) {
    font-size: 1.25rem;
  }
}

/* Print styles */
@media print {
  .toc-container {
    display: none;
  }
  
  .prose :where(img):not(:where([class~="not-prose"] *)) {
    max-width: 100% !important;
    height: auto !important;
  }
  
  .prose :where(a):not(:where([class~="not-prose"] *)) {
    text-decoration: underline;
  }
  
  .prose :where(a):not(:where([class~="not-prose"] *))::after {
    content: " (" attr(href) ")";
    font-size: 0.875rem;
    color: #666;
  }
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  * {
    transition: none !important;
    animation: none !important;
  }
  
  .prose :where(img):not(:where([class~="not-prose"] *)):hover {
    transform: none;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .prose :where(a):not(:where([class~="not-prose"] *)) {
    text-decoration: underline;
  }
  
  :deep(mark) {
    background: yellow !important;
    color: black !important;
  }
}
</style>